import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Mail, MapPin, Phone, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_NUMBER = "905313185280";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const waLink = () => {
    const text = [
      `Merhaba, İnci Tel'den teklif almak istiyorum.`,
      form.name ? `İsim: ${form.name}` : "",
      form.subject ? `Konu: ${form.subject}` : "",
      form.message ? `Detay: ${form.message}` : "",
    ]
      .filter(Boolean)
      .join("\n");
    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Lütfen isim, e-posta ve mesaj alanlarını doldurun.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data?.message || "Mesajınız alındı.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Mesaj gönderilemedi. Lütfen daha sonra tekrar deneyin."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="iletisim"
      data-testid="contact-section"
      className="relative bg-white py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] aspect-square rounded-full bg-[#FF5A00]/8 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF5A00]/10 text-[#FF5A00] font-mono-disp text-[11px] font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A00]" />
              İletişim
            </div>
            <h2 className="font-display mt-5 text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tighter text-gray-900 leading-[1.02]">
              Projeniz için{" "}
              <span className="font-serif-disp font-normal text-[#FF5A00]">
                teklif
              </span>{" "}
              alın.
            </h2>
            <p className="font-body mt-5 text-base text-gray-600 max-w-md">
              Galvanizli tel, bakır tel, örgü çit veya dikenli tel ihtiyacınız
              için uzmanlarımız 24 saat içinde size geri dönüş yapar.
            </p>

            <div className="mt-9 space-y-3">
              <a
                href="tel:+905313185280"
                data-testid="contact-phone"
                className="group flex items-center gap-4 p-4 rounded-2xl bg-[#fafafa] hover:bg-white hover:shadow-[0_8px_30px_-12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all"
              >
                <span className="w-11 h-11 grid place-items-center rounded-xl bg-[#FF5A00] text-white shadow-[0_6px_18px_-6px_rgba(255,90,0,0.7)]">
                  <Phone className="w-4 h-4" />
                </span>
                <div className="flex-1">
                  <div className="font-mono-disp text-[10px] text-gray-500 uppercase tracking-wider">
                    Telefon
                  </div>
                  <div className="font-display text-base font-semibold text-gray-900 mt-0.5 group-hover:text-[#FF5A00] transition-colors">
                    +90 531 318 52 80
                  </div>
                </div>
              </a>
              <a
                href="mailto:info@incicitsistemleri.com.tr"
                data-testid="contact-email"
                className="group flex items-center gap-4 p-4 rounded-2xl bg-[#fafafa] hover:bg-white hover:shadow-[0_8px_30px_-12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all"
              >
                <span className="w-11 h-11 grid place-items-center rounded-xl bg-gray-900 text-white">
                  <Mail className="w-4 h-4" />
                </span>
                <div className="flex-1">
                  <div className="font-mono-disp text-[10px] text-gray-500 uppercase tracking-wider">
                    E-posta
                  </div>
                  <div className="font-display text-base font-semibold text-gray-900 mt-0.5 group-hover:text-[#FF5A00] transition-colors">
                    info@incicitsistemleri.com.tr
                  </div>
                </div>
              </a>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="group flex items-center gap-4 p-4 rounded-2xl bg-[#25D366]/10 hover:bg-[#25D366]/15 border border-[#25D366]/20 transition-all"
              >
                <span className="w-11 h-11 grid place-items-center rounded-xl bg-[#25D366] text-white shadow-[0_6px_18px_-6px_rgba(37,211,102,0.6)]">
                  <MessageCircle className="w-4 h-4 fill-white" strokeWidth={0} />
                </span>
                <div className="flex-1">
                  <div className="font-mono-disp text-[10px] text-[#1DA851] uppercase tracking-wider font-medium">
                    WhatsApp
                  </div>
                  <div className="font-display text-base font-semibold text-gray-900 mt-0.5">
                    Hemen Mesaj Gönder
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-[#1DA851] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
              <div className="flex items-center gap-4 p-4 rounded-2xl bg-[#fafafa] border border-gray-100">
                <span className="w-11 h-11 grid place-items-center rounded-xl bg-gray-100 text-gray-700">
                  <MapPin className="w-4 h-4" />
                </span>
                <div className="flex-1">
                  <div className="font-mono-disp text-[10px] text-gray-500 uppercase tracking-wider">
                    Fabrika &amp; Merkez
                  </div>
                  <div className="font-display text-base font-semibold text-gray-900 mt-0.5">
                    İkitelli OSB, Başakşehir / İstanbul
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.form
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7 bg-white p-8 sm:p-10 rounded-[2rem] border border-gray-100 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.18)]"
          >
            <div className="font-mono-disp text-[10px] uppercase tracking-wider text-gray-400 mb-1">
              / Teklif Talep Formu
            </div>
            <h3 className="font-display text-2xl font-semibold tracking-tight text-gray-900 mb-6">
              Bize yazın
            </h3>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="İsim Soyisim"
                testId="contact-input-name"
                value={form.name}
                onChange={(v) => update("name", v)}
                required
              />
              <Field
                label="E-posta"
                type="email"
                testId="contact-input-email"
                value={form.email}
                onChange={(v) => update("email", v)}
                required
              />
              <Field
                label="Telefon"
                testId="contact-input-phone"
                value={form.phone}
                onChange={(v) => update("phone", v)}
              />
              <Field
                label="Konu"
                testId="contact-input-subject"
                value={form.subject}
                onChange={(v) => update("subject", v)}
              />
            </div>

            <div className="mt-4">
              <label className="font-mono-disp text-[10px] uppercase tracking-wider text-gray-500 font-medium">
                Mesajınız *
              </label>
              <textarea
                data-testid="contact-input-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={5}
                required
                className="mt-2 w-full bg-[#fafafa] border border-gray-200 focus:border-[#FF5A00] focus:bg-white outline-none font-body text-base px-4 py-3.5 rounded-2xl resize-none transition-all"
                placeholder="Talebinizi kısaca yazın..."
              />
            </div>

            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="btn-mod btn-primary btn-shift mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Gönderiliyor...
                </>
              ) : (
                <>
                  Teklif Talep Et
                  <ArrowUpRight className="arrow w-4 h-4" />
                </>
              )}
            </button>
            <p className="mt-3 font-mono-disp text-[10px] text-gray-400 uppercase tracking-wider">
              * ile işaretli alanlar zorunludur.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="font-mono-disp text-[10px] uppercase tracking-wider text-gray-500 font-medium">
        {label} {required && "*"}
      </label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-[#fafafa] border border-gray-200 focus:border-[#FF5A00] focus:bg-white outline-none font-body text-base px-4 py-3.5 rounded-2xl transition-all"
      />
    </div>
  );
}
